import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const category = useMemo(() => categories.find(c => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter(c => c.category === slug);
    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else {
      result.sort((a, b) => b.views - a.views);
    }
    return result;
  }, [slug, sortBy]);

  const pageTitle = category ? category.name : '全部作品';

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <span className="text-foreground-400">/</span>
              <span className="text-foreground-800 font-medium">{pageTitle}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {pageTitle}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {category
                ? `妖精漫画免费阅读韩漫官网 - ${category.name}分类，海量韩国漫画作品任你选`
                : '妖精漫画免费阅读韩漫官网 - 全部韩国漫画作品，海量韩漫任你选'}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/60">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/category/all"
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  slug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.filter(c => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">共 {filteredComics.length} 部作品</p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setSortBy('latest')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'latest' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新更新
              </button>
              <button
                onClick={() => setSortBy('popular')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'popular' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最受欢迎
              </button>
            </div>
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-read-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览全部作品
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative overflow-hidden rounded-lg mb-3">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 妖精漫画免费阅读`}
                      title={`${comic.title} - 妖精漫画免费韩漫`}
                      className="w-full aspect-[3/4] object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2">
                      {comic.status === '连载中' ? (
                        <span className="px-2 py-0.5 rounded-md bg-green-500/90 text-white text-xs">连载</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md bg-foreground-500/80 text-white text-xs">完结</span>
                      )}
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-xs" style={{ color: `oklch(var(--accent-500))` }}></i>
                    </div>
                    <span className="text-xs text-foreground-500">{comic.rating}</span>
                    <span className="text-xs text-foreground-300 mx-1">|</span>
                    <span className="text-xs text-foreground-400">{comic.author}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}